import React, { useEffect, useMemo, useState } from "react";

// Utils
import { Header, Modal, ModalContent } from "semantic-ui-react";

import { ADMIN_NAME_LIST } from "react-lib/apps/USER/CardSettingDivision";

// UX
import CardClinicAnnualReportUX from "./CardClinicAnnualReportUX";
import CardClinicIncomeReportFilter from "./CardClinicIncomeReportFilter";
import CardDoctorFeeByPaymentFilter from "./CardDoctorFeeByPaymentReportFilter";
import CardDoctorIncomeMonthlySummaryFilter from "./CardDoctorIncomeMonthlySummaryFilter";
import CardDoctorIncomeSummaryFilter from "./CardDoctorIncomeSummaryFilter";
import CardPaidCompensationWorkingTypeFilter from "./CardPaidCompensationWorkingTypeFilter";
import CardPatientPaidRiskReportFilter from "./CardPatientPaidRiskReportFilter";
import CardPatientPaidServicesReportFilter from "./CardPatientPaidServicesReportFilter";
import CardPatientSaveRiskReportFilter from "./CardPatientSaveRiskReportFilter";
import CardPriceChangeOverviewSummaryFilter from "./CardPriceChangeOverviewSummaryFilter";
import CardPriceChangeRiskReportFilter from "./CardPriceChangeRiskReportFilter";
import CardClinicRevenueReportFilter from "./CardClinicRevenueReportFilter";

// Interface
import { PickedState, State } from "./sequence/ClinicAnnualReport";

// Props
type CardClinicAnnualReportProps = Pick<PickedState, "adminMenuDivisions" | "selectedDivision"> & {
  onEvent: (e: any) => any;
  setProp: any;
  runSequence: any;
  buttonLoadCheck: Record<string, any>;
  // Sequence
  ClinicAnnualReportSequence?: State["ClinicAnnualReportSequence"];
  masterOptions?: Record<string, any>;
  languageUX: "en" | "th";
};

const CardClinicAnnualReport = (props: CardClinicAnnualReportProps) => {
  // State
  const [openModal, setOpenModal] = useState(false);

  // Call Sequence
  useEffect(() => {
    const divisionId =
      props.adminMenuDivisions?.all[ADMIN_NAME_LIST.รายงานประจำคลินิก]?.ids[props.selectedDivision?.id] || null;

    props.runSequence({
      sequence: "ClinicAnnualReport",
      divisionId,
      restart: true,
    });
  }, [props.selectedDivision?.id]);

  const divisionOptions = useMemo(
    () => props.adminMenuDivisions?.all[ADMIN_NAME_LIST.รายงานประจำคลินิก]?.divisions || [],
    [props.adminMenuDivisions, props.selectedDivision]
  );

  // console.log("ClinicAnnual Report Props: ", props);
  // console.log("ClinicAnnual Report MasterData: ", props.masterOptions);

  // Call Sequence (GET_REPORT)
  const callGetReportSequence = (menu: string) => {
    props.runSequence({
      sequence: "ClinicAnnualReport",
      action: "GET_REPORT",
      menu,
    });
  };

  // Select Menu
  const handleSelectMenu = (menu: string) => {
    // console.log("ClinicAnnual Report Menu: ", menu);
    setOpenModal(true);
    callGetReportSequence(menu);
  };

  // Close Modal
  const handleCloseModal = () => {
    props.runSequence({
      sequence: "ClinicAnnualReport",
      divisionId: props.ClinicAnnualReportSequence?.selectedDivisionId,
      restart: true,
    });

    setOpenModal(false);
  }

  // Get Filter Content
  const getFilterModalContent = useMemo(() => {
    switch (props.ClinicAnnualReportSequence?.selectedMenu) {
      case "รายงานสรุปรายได้ และค่าตอบแทนแพทย์":
        return (
          <CardDoctorIncomeSummaryFilter
            runSequence={props.runSequence}
            setProp={props.setProp}
            ClinicAnnualReportSequence={props.ClinicAnnualReportSequence}
            masterOptions={props.masterOptions}
            buttonLoadCheck={props.buttonLoadCheck}
            languageUX={props.languageUX}
          />
        );
      case "รายงานสรุปรายได้ทั้งเดือนส่งเบิกจ่ายแพทย์":
        return (
          <CardDoctorIncomeMonthlySummaryFilter
            runSequence={props.runSequence}
            setProp={props.setProp}
            ClinicAnnualReportSequence={props.ClinicAnnualReportSequence}
            masterOptions={props.masterOptions}
            buttonLoadCheck={props.buttonLoadCheck}
            languageUX={props.languageUX}
          />
        );
      case "รายงานการแบ่งจ่ายค่าตอบแทนตามประเภทงาน":
        return (
          <CardPaidCompensationWorkingTypeFilter
            runSequence={props.runSequence}
            setProp={props.setProp}
            ClinicAnnualReportSequence={props.ClinicAnnualReportSequence}
            masterOptions={props.masterOptions}
            buttonLoadCheck={props.buttonLoadCheck}
            languageUX={props.languageUX}
          />
        );
      case "รายงานค่ารักษาที่แพทย์เปลี่ยนแปลงราคา":
        return (
          <CardPriceChangeRiskReportFilter
            runSequence={props.runSequence}
            setProp={props.setProp}
            ClinicAnnualReportSequence={props.ClinicAnnualReportSequence}
            masterOptions={props.masterOptions}
            buttonLoadCheck={props.buttonLoadCheck}
            languageUX={props.languageUX}
          />
        );
      case "รายงานภาพรวมการเปลี่ยนแปลงราคาโดยแพทย์":
        return (
          <CardPriceChangeOverviewSummaryFilter
            runSequence={props.runSequence}
            setProp={props.setProp}
            ClinicAnnualReportSequence={props.ClinicAnnualReportSequence}
            masterOptions={props.masterOptions}
            buttonLoadCheck={props.buttonLoadCheck}
            languageUX={props.languageUX}
          />
        );
      case "รายงานสรุปค่าใช้จ่ายผู้ป่วยเข้ารับบริการที่คลินิก":
        return (
          <CardClinicIncomeReportFilter
            runSequence={props.runSequence}
            setProp={props.setProp}
            ClinicAnnualReportSequence={props.ClinicAnnualReportSequence}
            masterOptions={props.masterOptions}
            buttonLoadCheck={props.buttonLoadCheck}
            languageUX={props.languageUX}
          />
        );
      case "รายงานผู้ป่วยค้างชำระ":
        return (
          <CardPatientPaidRiskReportFilter
            runSequence={props.runSequence}
            setProp={props.setProp}
            ClinicAnnualReportSequence={props.ClinicAnnualReportSequence}
            masterOptions={props.masterOptions}
            buttonLoadCheck={props.buttonLoadCheck}
            languageUX={props.languageUX}
          />
        );
      case "รายงานผู้ป่วยที่ยังไม่ได้บันทึกรับชำระ":
        return (
          <CardPatientSaveRiskReportFilter
            runSequence={props.runSequence}
            setProp={props.setProp}
            ClinicAnnualReportSequence={props.ClinicAnnualReportSequence}
            masterOptions={props.masterOptions}
            buttonLoadCheck={props.buttonLoadCheck}
            languageUX={props.languageUX}
          />
        );
      case "รายงานผู้ป่วยที่ชำระค่าบริการ":
        return (
          <CardPatientPaidServicesReportFilter
            runSequence={props.runSequence}
            setProp={props.setProp}
            ClinicAnnualReportSequence={props.ClinicAnnualReportSequence}
            masterOptions={props.masterOptions}
            buttonLoadCheck={props.buttonLoadCheck}
            languageUX={props.languageUX}
          />
        );
      case "รายงานค่าตอบแทนแพทย์ตามวันที่รับชำระ":
        return (
          <CardDoctorFeeByPaymentFilter
            isClinic={true}
            buttonAction="DOWNLOAD_REPORT"
            runSequence={props.runSequence}
            setProp={props.setProp}
            sequence={props.ClinicAnnualReportSequence}
            sequenceName="ClinicAnnualReportSequence"
            runSequenceName="ClinicAnnualReport"
            cardKeyButton="ClinicAnnualReport"
            buttonLoadCheck={props.buttonLoadCheck}
            masterOptions={props.masterOptions}
            languageUX={props.languageUX}
          />
        );
      case "รายงานสรุปรายได้คลินิก":
        return (
          <CardClinicRevenueReportFilter
            runSequence={props.runSequence}
            setProp={props.setProp}
            ClinicAnnualReportSequence={props.ClinicAnnualReportSequence}
            masterOptions={props.masterOptions}
            buttonLoadCheck={props.buttonLoadCheck}
            languageUX={props.languageUX}
          />
        );
      default:
        return null;
    }
  }, [props.ClinicAnnualReportSequence, props.masterOptions, props.buttonLoadCheck]);

  const handleModalSize = () : "small" | "large" | "mini" | "tiny" | "fullscreen" | undefined => {
    switch (props.ClinicAnnualReportSequence?.selectedMenu) {
      case "รายงานสรุปรายได้ และค่าตอบแทนแพทย์":
      case "รายงานสรุปรายได้ทั้งเดือนส่งเบิกจ่ายแพทย์":
      case "รายงานการแบ่งจ่ายค่าตอบแทนตามประเภทงาน":
      case "รายงานค่ารักษาที่แพทย์เปลี่ยนแปลงราคา":
      case "รายงานภาพรวมการเปลี่ยนแปลงราคาโดยแพทย์":
      case "รายงานสรุปค่าใช้จ่ายผู้ป่วยเข้ารับบริการที่คลินิก":
      case "รายงานผู้ป่วยค้างชำระ":
      case "รายงานผู้ป่วยที่ยังไม่ได้บันทึกรับชำระ": // return "large";
      case "รายงานผู้ป่วยที่ชำระค่าบริการ":
      case "รายงานสรุปรายได้คลินิก": {
        return "small";
      }

      // case "รายงานค่าตอบแทนแพทย์ตามวันที่รับชำระ":
      //   return "large";
      default: {
        return undefined;
      }
    }
  };

  return (
    <>
      <CardClinicAnnualReportUX
        languageUX={props.languageUX}
        selectedDivisionId={props.ClinicAnnualReportSequence?.selectedDivisionId}
        divisionOptions={divisionOptions}
        onChangeDivision={(e: any, v: any) => {
          props.setProp("ClinicAnnualReportSequence.selectedDivisionId", v.value);
        }}
        onHandleSelectedMenu={handleSelectMenu}
      />

      <Modal
        onClose={() => {
          handleCloseModal();
        }}
        open={openModal}
        size={handleModalSize()}
      >
        <Header>{props.ClinicAnnualReportSequence?.selectedMenu}</Header>
        <ModalContent style={{ padding: "1rem" }}>{getFilterModalContent}</ModalContent>
      </Modal>
    </>
  );
};

export default CardClinicAnnualReport;
